import { CartClientV2, getFirst, isValid, isValidWithoutData } from 'clients';
import { ARR_REMOVE_PRODUCT, MONITORING_COLLECTOR_TYPE } from 'constants/Enums';
import { ERROR_CODE_CART } from 'constants/ErrorCart';
import { CHECKOUT_URL } from 'constants/Paths';
import { useAuth } from 'context/Auth';
import { useProduct } from 'context/Product/context';
import { useModal } from 'hooks';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { CartService } from 'services';
import { MAX_ITEM_IN_CART } from 'sysconfig';
import { NotifyUtils, gtag } from 'utils';
import { useEnhancedReducer } from 'utils/EnhanceReducer';
import { formatCurrency } from 'utils/FormatNumber';
import { getProxyImageList } from 'utils/ImageUtils';
import MonitorUtils, { mapScreenToEnum, mapSourceToEnum } from 'utils/MonitorUtils';
import { capitalizeText } from 'utils/StringUtils';
import { isEmpty } from 'utils/ValidateUtils';
import fbpixel from 'utils/fbpixel';
import useSellers from 'zustand-lib/useSellers';
import { buildCartFluctuates, CartReducer } from './CartReducer';
import {
  CHECKOUT,
  CLEAR,
  FETCH_ERROR,
  FETCH_SUCCESS,
  INCREASE_BY,
  SELECT_ALL_ITEM,
  SELECT_ITEM,
  SET_CART_FLUCTUATES,
  UN_SELECT_ALL_ITEM,
} from './CartType';

export const CartContext = createContext();

export const CartContextProvider = ({ children }) => {
  const { t: translateCommon } = useTranslation('common');
  const { t: translateAlertAction } = useTranslation('alert-action');
  const { isAuthenticated } = useAuth();
  const router = useRouter();
  const { getProductBySkus } = useProduct();
  // mapQuantity có sẵn từ đầu: trang SP render cho khách ngay từ server (SSR_GUEST_CONTENT_ROUTES ở AuthProvider),
  // không còn màn loading chờ effect clearCart() tạo mapQuantity
  const initialState = { loading: true, mapQuantity: new Map() };
  const [isShowModalMax200Item, toggleShowModalMax200Item] = useModal();
  const getSellerByCode = useSellers((state) => state.getSellerByCode);

  const giftProductRowRef = useRef(null);

  const [isLoadingCart, setIsLoadingCart] = useState(false);
  const [isDeletingPromo, setDeletingPromo] = useState(false);
  const [isOpenPromoList, togglePromoList] = useState(false);
  const [isOpenPriceFluctuationModal, setOpenPriceFluctuationModal] = useState(false);
  // Modal biến động giá ở /cart mở từ 2 nguồn: alert (xác nhận rồi ở lại giỏ) và nút thanh
  // toán (xác nhận rồi đi tiếp /checkout). Cờ này giữ ý định của nguồn đã mở modal.
  const [shouldCheckoutAfterConfirm, setShouldCheckoutAfterConfirm] = useState(false);
  const [tempRedeemApplyResult, setTempRedeemApplyResult] = useState([]);
  const [isOnCheckingPromo, toggleOnCheckingPromo] = useState(false);
  const [isShowRemoveInvalidPromos, toggleShowRemoveInvalidPromos] = useState(false);
  const [isRemovingInvalidPromos, setIsRemovingInvalidPromos] = useState(false);

  // const [state, dispatch] = useReducer(CartReducer, initialState);

  // enhance cartReducer support function getState
  const [state, dispatch, getState] = useEnhancedReducer(CartReducer, initialState);
  const clearCart = () => {
    dispatch({ type: CLEAR });
    setIsLoadingCart(false);
  };

  const handleCheckout = () => {
    dispatch({ type: CHECKOUT });
  };

  // const getPromoInfo = useCallback(async ({ voucherCode }) => {
  //   if (!voucherCode) {
  //     return null;
  //   }
  //   const promoData = await PromoService.getPromotionDetailByVoucherCode({ voucherCode });
  //   return promoData;
  // });

  // business_client , GPP , business certificate
  // eslint-disable-next-line no-unused-vars
  const getErrorCart = ({ errorCode, errorMessage = null }) =>
    translateAlertAction(`message_error_cart.${errorCode}`);
  // console.log('🚀 ~ file: CartContext.js ~ line 54 ~ getErrorCart ~ errorCode, errorMessage', errorCode, errorMessage);
  // if (errorCode === ERROR_CODE_CART.REQUIRED_CERTIFICATE) {
  //   let message = 'Sản phẩm yêu cầu giấy phép: ';
  //   if (errorMessage?.indexOf('GPP') >= 0) {
  //     message += 'GPP';
  //   }
  //   if (errorMessage?.indexOf('BUSSINESS_CLIENT') >= 0) {
  //     message += ' giấy đủ điều kiện kinh doanh dược';
  //   }
  //   if (errorMessage?.indexOf('BUSSINESS_CERTIFICATE') >= 0) {
  //     message += ' giấy hoạt động phòng khám ';
  //   }
  //   return message;
  // }
  // ERROR_CART[errorCode];
  const getInfoCartItem = useCallback(async ({ data, getPrice }) => {
    if (isEmpty(data)) {
      return [];
    }
    const skusReduce = data.reduce((accumulator, item) => {
      if (item?.sku) return [...accumulator, item.sku];
      return accumulator;
    }, []);

    if (skusReduce.length === 0) {
      return [];
    }
    const mapInfo = await getProductBySkus({ skus: skusReduce, getPrice });

    // sort những item nào có lỗi sẽ hiện lên trước
    let cartItems = data.map((item) => {
      const { quantity, skuStatus } = item || {};

      // TODO: ẩn nhà cung cấp
      const {
        imagesProxy,
        unit,
        volume,
        name,
        slug,
        deal = {},
        isDeal,
        isGift,
        skuType,
        productId,
        defaultImage,
        displayPrice,
        salePrice,
        dealPrice,
        requireGPPMessage,
        isCampaign,
        availableProducts,
        tags,
        productSkuType,
        weight,
        productVolume,
        statusData,
        limitPerDay,
        maxQuantityPerDay,
        quantityPurchasedToday,
        discountPercent,
        messageLimitOrder,
        categoryCodes,
        campaign,
        isContractPrice,
        skus,
      } = mapInfo.get(item.sku) || {};

      let { errorCode = '' } = item;
      const isNearOutOfStock = skuStatus === 'LIMIT' || false;
      if (
        !errorCode &&
        maxQuantityPerDay > 0 &&
        quantity > maxQuantityPerDay - quantityPurchasedToday
      ) {
        // eslint-disable-next-line no-param-reassign
        errorCode = 'MAX_QUANTITY';
      }

      // const salePrice = deal?.dealType === 'COMBO' || !deal ? item.price : item.salePrice;
      const disableAddTocart =
        errorCode === 'NOT_ACTIVE_SKU' ||
        errorCode === 'SUSPENDED_SKU' ||
        errorCode === 'OUT_OF_STOCK_SKU' ||
        errorCode === 'CART_ITEM_INVALID' ||
        errorCode === 'NOT_AVAILABLE_SKU' ||
        errorCode === 'NOT_FOUND_SKU' ||
        errorCode === 'NOT_FOUND_PRICE_SKU' ||
        errorCode === 'COMBO_INVALID' ||
        errorCode === ERROR_CODE_CART.REQUIRED_CERTIFICATE ||
        false;

      /*
      // nếu item (cart item ) là deal thì có thêm 1 field là  salePrice ( giá gốc )
      // nếu không là deal thì chỉ có price 
      nhưng khi hiển thị ở cartProductBuy lại chỉ check salePrice , nên nếu 
      Updaate chỗ này để nếu có saleprice thì lấy giá saleprice , còn nếu ko có thì lấy giá price theo cartItem 
      salePrice: item.salePrice || item.price,
  */
      const info = {
        ...item,
        salePrice,
        errorCode,
        defaultImage,
        displayPrice,
        displayPriceFormated: formatCurrency(displayPrice),
        dealPrice,
        imagesProxy,
        volume,
        unit,
        name,
        slug,
        deal,
        isDeal,
        isGift,
        disableAddTocart,
        skuType,
        productId, // for insider
        requireGPPMessage,
        isCampaign,
        availableProducts,
        productTags: tags || [],
        tags,
        productSkuType,
        weight,
        productVolume,
        statusData,
        isVAT: tags?.indexOf('HOADONNHANH') >= 0 || false,
        limitPerDay,
        maxQuantityPerDay,
        quantityPurchasedToday,
        discountPercent,
        messageLimitOrder,
        categoryCodes,
        campaign,
        isNearOutOfStock,
        isContractPrice,
        skus,
      };
      // khi add khuyến mãi là các gift quà tặng thì auto sellected là false luôn,
      function isGiftSellect(giftSellected) {
        if (giftSellected) return Object.assign(info, { isSelected: false });
        return null;
      }
      isGiftSellect(isGift);
      return info;
    });

    cartItems = cartItems.sort((a, b) => (a.errorCode && !b.errorCode ? -1 : 1));

    return cartItems;
  });

  // const getTotalCartItem = async () => {};
  // const updateCartFirstTime = async () => {};

  const getCartItemsInfo = async (cartData = {}) => {
    const { cartItems = [] } = cartData || {};
    const [cartItemsInfo] = await Promise.all([
      getInfoCartItem({ data: cartItems, getPrice: true }),
    ]);

    return { ...cartData, cartItems: cartItemsInfo };
  };

  // reload cart
  const reloadDataCart = async ({
    cartRes,
    successMessage,
    errorMessage,
    isUpdate = false,
    isLoadFromStart = false,
  }) => {
    try {
      if (isLoadFromStart) setIsLoadingCart(true);
      if (cartRes && !isValidWithoutData(cartRes)) {
        const [cartResponse] = getFirst(cartRes, []);
        switch (cartRes.errorCode) {
          case 'MAX_QUANTITY':
            // [ [ {quantity} ] ]
            if (cartResponse?.quantity > 0) {
              NotifyUtils.error(
                `${translateAlertAction('message_product.max_quantity_greater_than_0')} ${cartResponse.quantity}`,
              );
            } else {
              NotifyUtils.error(translateAlertAction('message_product.max_quantity'));
            }
            break;
          case 'PAYLOAD_VALIDATE':
            if (cartRes.message === 'Type must be one of [NORMAL DEAL COMBO CAMPAIGN]') {
              NotifyUtils.error(translateAlertAction('message_not_add_gift_to_cart'));
            } else {
              NotifyUtils.error(translateAlertAction('error_pls_try_again'));
            }
            break;
          default:
            if (cartRes && cartRes.message) {
              NotifyUtils.error(`${cartRes.message}`);
            } else if (errorMessage) {
              NotifyUtils.error(errorMessage);
            }
            return;
        }
      }
      if (successMessage) NotifyUtils.success(successMessage);

      const cartResult = await CartClientV2.loadDataCart();

      const cartData = getFirst(cartResult);
      if (!cartData) {
        clearCart();
        return;
      }

      /*
       chỉ trang cart cần load lại data product , còn  khi các action khác chỉ cần update lại thông tin cartItem thôi là dc
      */
      const {
        cartItems = [],
        redeemApplyResult = [],
        maxWeight = 0,
        maxVolume = 0,
      } = cartData || {};

      // const giftArray = redeemApplyResult?.[0]?.gifts || [];
      const hasGiftArray = redeemApplyResult
        ?.filter((item) => item?.gifts && item?.canUse === true)
        ?.map((item) => ({
          ...item,
          gifts: item?.gifts.map((gift) => ({ ...gift })),
        }));
      const giftList = [];
      hasGiftArray.forEach((ele) => {
        giftList.push(...ele.gifts);
      });
      const arrayHashmap = giftList.reduce((obj, item) => {
        const newObj = obj;

        if (newObj[item?.sku]) {
          newObj[item.sku].quantity += item.quantity;
        } else {
          newObj[item.sku] = { ...item };
        }

        return newObj;
      }, {});

      const giftArray = Object.values(arrayHashmap);
      const [cartItemsInfo, giftInfo] = await Promise.all([
        getInfoCartItem({ data: cartItems, getPrice: true }),
        // getPromoInfo({ voucherCode: redeemCode[0] }),
        getInfoCartItem({ data: giftArray, getPrice: false }),
      ]);

      // thêm isGift vào để phân biệt
      const giftsInfo = giftInfo?.map((item) => ({ ...item, isGift: true, type: 'GIFT' }));

      cartData.cartItems = [...cartItemsInfo, ...giftsInfo];
      setTempRedeemApplyResult(cartData?.redeemApplyResult); // init tempRedeemApplyResult

      const fluctuatedItems = cartData?.fluctuatedItems || [];
      cartData.fluctuatedItems = fluctuatedItems;

      const mergedFluctuates = buildCartFluctuates(cartData.cartItems, fluctuatedItems);
      cartData.cartItems = mergedFluctuates.cartItems;
      cartData.cartFluctuates = mergedFluctuates.cartFluctuates;

      /* Check error cart item */
      cartData.isErrorCartItem =
        cartData?.cartItems?.some(
          (item) =>
            item?.isSelected &&
            item?.errorCode &&
            item?.errorCode !== ERROR_CODE_CART.CHANGED_PRICE,
        ) || false;
      cartData.havingItemSelected = !!cartData?.cartItems?.find(
        (item) => item?.isSelected === true,
      );
      cartData.isItemInArrayRemove =
        cartData?.cartItems?.some((item) => ARR_REMOVE_PRODUCT.indexOf(item?.errorCode) >= 0) ||
        false;
      if (maxVolume > 0 || maxWeight > 0) {
        const { totalWeight, totalVolume, maxWidth, maxHeight, maxLength } = cartData || {};

        const isOverWeightOrVolume =
          (maxWeight > 0 && totalWeight >= maxWeight) ||
          (maxVolume > 0 && totalVolume >= maxVolume);
        cartData.isOverWeightOrVolume = isOverWeightOrVolume;
        let messageOverWeight = '';
        let messageOverVolume = '';
        if (maxWeight > 0 && totalWeight >= maxWeight) {
          // messageOverWeight = `Giỏ hàng hiện tại vượt quá ${maxWeight}kg.`;
          messageOverWeight = translateAlertAction('message_over_max_weight').replace(
            '{weight}',
            maxWeight,
          );
          if (isUpdate)
            NotifyUtils.error(
              translateAlertAction('message_over_weight_cart').replace('{weight}', maxWeight),
            );
        }
        if (maxVolume > 0 && totalVolume >= maxVolume) {
          let maxVolumeCalc = '';
          if (maxWidth > 0 && maxLength > 0 && maxHeight > 0) {
            maxVolumeCalc = `${maxLength}x${maxWidth}x${maxHeight}`;
          }
          // messageOverVolume = `Giỏ hàng hiện tại vượt quá quy cách đóng gói ${maxVolumeCalc}`;
          messageOverVolume = translateAlertAction('message_over_packing').replace(
            '{volume}',
            maxVolumeCalc,
          );
          if (totalVolume >= maxVolume) {
            NotifyUtils.error(
              translateAlertAction('message_over_packing').replace('{volume}', maxVolumeCalc),
            );
          }
        }
        if (isOverWeightOrVolume)
          cartData.messageOverWeightOrVolume = `${messageOverWeight}${messageOverVolume}`;
      }

      setIsLoadingCart(false);
      dispatch({ type: FETCH_SUCCESS, payload: cartData || [] });
    } catch (error) {
      NotifyUtils.error(error.message || translateAlertAction('message_loading_cart_failed'));
      dispatch({ type: FETCH_ERROR });
      setIsLoadingCart(false);
    }
  };

  // TODO: change to reloadCart
  const updateCart = useCallback(async () => {
    // const cartRes = await CartClientV2.loadDataCart();
    // if (!isValid(cartRes)) {
    //   dispatch({ type: FETCH_ERROR });
    //   return;
    // }
    await reloadDataCart({ isLoadFromStart: true });
  }, []);

  useEffect(() => {
    async function fetchData() {
      await updateCart();
    }
    try {
      if (isAuthenticated) {
        fetchData();
      } else {
        // logout remove cart
        clearCart();
      }
    } catch (error) {
      clearCart();
    }
  }, [updateCart, isAuthenticated]);

  const mapDataProduct = (products = []) => {
    const { mapQuantity = new Map() } = getState();
    return products.map((item) => ({
      ...item,
      imagesProxy: getProxyImageList(item?.imageUrls) || [],
      // unit: item.unit && item.unit === '<nil>' ? '' : item.unit,
      quantity: mapQuantity.get(item.sku) || 0,
    }));
  };

  const updateCartItem = async (payload, reload = false, isReloadData = false, isNotify = true) => {
    if (!payload?.product?.sku && isNotify) {
      NotifyUtils.error(translateAlertAction('message_cannot_found_product_add'));
      return null;
    }

    const { cartItems, cartNo, mapQuantity } = getState();

    const info = cartItems?.find((item) => item.sku === payload?.product?.sku);
    const sellerInfo = await getSellerByCode({ code: payload?.product?.sellerCode });

    const dataUpdate = { ...payload, cartNo, sellerID: sellerInfo?.sellerID || null };

    dataUpdate.page = info?.page || router.pathname.replace('/', '') || 'home';

    dataUpdate.searchKey = info ? info.searchKey : router.query?.q || router.query?.text;

    dataUpdate.eventSource = mapSourceToEnum(payload?.product || {}, router.pathname) || '';
    dataUpdate.eventScreen = mapScreenToEnum(payload?.product || {}, router.pathname) || '';

    let cartRes = await CartClientV2.updateCartItem(dataUpdate);

    const oldQuantity = mapQuantity.get(payload?.product?.sku) ?? 0;
    if (reload) {
      reloadDataCart({
        cartRes,
        isReloadData,
        isUpdate: true,
      });
    }

    if (isValid(cartRes)) {
      // FB event tracking addToCart
      fbpixel.addToCart(payload.product);

      if (!info?.quantity && payload?.product?.isRecommendedByTS) {
        // 0 -> 1: send tracking collector event
        MonitorUtils.sendSKUEvent(
          MONITORING_COLLECTOR_TYPE.ADD_TO_CART,
          { ...payload.product, quantity: dataUpdate?.q || 0 },
          window.location.href,
        );
      }

      const { quantity, totalQuantity } = cartRes.data[0];
      const newQuantity = quantity || totalQuantity;
      const product = {
        ...payload.product,
        quantity: Math.abs(newQuantity - oldQuantity),
        sellerInfo,
      };
      if (newQuantity > oldQuantity) {
        gtag.addToCartInPage(product, router.pathname);
      } else {
        gtag.removeFromCart(product);
      }
      // gtag.addToCart({ ...payload.product, quantity: dataUpdate?.q || 0, sellerInfo });
      // gtag.addToCartInPage({ ...payload.product, quantity: dataUpdate?.q || 0, sellerInfo }, router.pathname);

      if (isNotify) {
        NotifyUtils.success(
          `${translateAlertAction('message_update_product_success').replace('{product}', dataUpdate.product.name)}`,
        );
      }
    } else if (cartRes.errorCode === 'CART_MAX_QUANTITY' || cartRes.errorCode === 'MAX_QUANTITY') {
      try {
        // get quanity can add from response and compare with maxQuantity
        const [{ quantity = dataUpdate.product.maxQuantity }] = getFirst(cartRes, []);
        dataUpdate.q = quantity;
        cartRes = await CartClientV2.updateCartItem(dataUpdate);
        // if (isValid(cartRes)) {
        //   NotifyUtils.success(`Đã cập nhật ${capitalizeText(dataUpdate.product.name)} thành công`);
        // } else {
        //   NotifyUtils.error(`Cập nhật sản phẩm thất bại`);
        // }
        dispatch({ type: INCREASE_BY, payload: dataUpdate });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }

    return cartRes;
  };

  // clone from updateCardItem add skip call API when payload larger than the quantity required
  const updateCartItemMV2 = async (payload, reload = false, isReloadData = false) => {
    if (!payload?.product?.sku) {
      NotifyUtils.error(translateAlertAction('message_cannot_found_product_add'));
      return null;
    }

    const { cartItems, cartNo, mapQuantity } = getState();
    const info = cartItems?.find((item) => item.sku === payload?.product?.sku);

    const dataUpdate = { ...payload, cartNo };

    dataUpdate.page = info?.page || router.pathname.replace('/', '') || 'home';

    dataUpdate.searchKey = info ? info.searchKey : router.query?.q || router.query?.text;

    dataUpdate.eventSource = mapSourceToEnum(payload?.product || {}, router.pathname) || '';
    dataUpdate.eventScreen = mapScreenToEnum(payload?.product || {}, router.pathname) || '';

    const oldQuantity = mapQuantity.get(payload?.product?.sku) ?? 0;
    const cartRes = await CartClientV2.updateCartItem(dataUpdate);

    if (reload) {
      reloadDataCart({
        cartRes,
        isReloadData,
        isUpdate: true,
      });
    }

    if (isValid(cartRes)) {
      // FB event tracking addToCart
      fbpixel.addToCart(payload.product);

      const { quantity, totalQuantity } = cartRes.data[0];
      const newQuantity = quantity || totalQuantity;
      const sellerInfo = await getSellerByCode({ code: payload.product?.sellerCode });
      const product = {
        ...payload.product,
        quantity: Math.abs(newQuantity - oldQuantity),
        sellerInfo,
      };
      if (newQuantity > oldQuantity) {
        gtag.addToCartInPage(product, router.pathname);
      } else {
        gtag.removeFromCart(product);
      }
      // gtag.addToCart({ ...payload.product, quantity: dataUpdate?.q || 0 });
      // gtag.addToCartInPage({ ...payload.product, quantity: dataUpdate?.q || 0 }, router.pathname);

      NotifyUtils.success(
        `${translateAlertAction('message_update_product_success').replace('{product}', dataUpdate.product.name)}`,
      );
      // NotifyUtils.success(`Đã cập nhật ${capitalizeText(dataUpdate.product.name)} thành công`);
    } else if (cartRes.errorCode === 'CART_MAX_QUANTITY' || cartRes.errorCode === 'MAX_QUANTITY') {
      return {
        status: cartRes.errorCode,
        maxQuantity: getFirst(cartRes, [0])[0]?.quantity || 0,
      };
    }

    return cartRes;
  };

  // update for excel
  const updateCartItems = async (items) => {
    items.forEach((item) => {
      const { sku, quantity } = item || {};
      updateCartItem({ product: { sku }, q: quantity });
    });
  };
  const updateCartItemByExecel = (str) => {
    const arr = str.replaceAll('\t', ' ').split(' ');
    const items = [];
    for (let i = 0; i < arr.length > 0; i += 2) {
      const sku = arr[i].trim();
      const quantity = Number(arr[i + 1]);
      items.push({ sku, quantity });
    }
    updateCartItems(items);
  };

  // update for excel
  const increase = async (payload) => {
    const cartRes = await CartClientV2.updateCartItem({
      ...payload,
      eventSource: mapSourceToEnum({}, router.pathname) || '',
      eventScreen: mapScreenToEnum({}, router.pathname) || '',
    });
    reloadDataCart({
      cartRes,
      successMessage: translateAlertAction('message_add_products_successfully'),
      errorMessage: translateAlertAction('message_update_product_failed'),
    });
  };

  const decrease = async (payload) => {
    const cartRes = await CartClientV2.updateCartItem({
      ...payload,
      eventSource: mapSourceToEnum({}, router.pathname) || '',
      eventScreen: mapScreenToEnum({}, router.pathname) || '',
    });
    reloadDataCart({
      cartRes,
      successMessage: translateAlertAction('message_cart_updated'),
      errorMessage: translateAlertAction('message_update_product_failed'),
    });
  };

  const removeCartItem = async (
    { sku, isRecommended = false, isSameCategoryProduct = false },
    isReloadData = false,
    isShowMessage = true,
  ) => {
    const payload = { sku };
    const { cartItems, cartNo, mapQuantity } = getState();

    const info = cartItems.find((item) => item.sku === sku);

    // :TODO: LOGS info for activity
    payload.cartNo = cartNo;
    payload.name = info?.name;
    payload.slug = info?.slug;
    // payload.productCode = info?.productCode;
    payload.productId = info?.productId;
    payload.salePrice = info?.salePrice;
    payload.sellerCode = info?.sellerCode;
    payload.cartNo = info?.cartNo;
    payload.cartItemType = info?.cartItemType;
    payload.type = info?.type || null;
    payload.eventSource =
      mapSourceToEnum({ isRecommended, isSameCategoryProduct }, router.pathname) || '';
    payload.eventScreen =
      mapScreenToEnum({ isRecommended, isSameCategoryProduct }, router.pathname) || '';
    // end log

    const sellerInfo = await getSellerByCode({ code: payload?.sellerCode });

    const cartRes = await CartClientV2.removeCartItem({
      ...payload,
      sellerID: sellerInfo?.sellerID || null,
    });

    const quantity = mapQuantity.get(sku);
    gtag.removeFromCart({
      ...info,
      quantity,
    });

    // if (cartRes?.status === 'OK') {
    //   MonitorUtils.sendSKUEvent(MONITORING_COLLECTOR_TYPE.REMOVE_FROM_CART, payload);
    // }

    if (isReloadData) {
      reloadDataCart({
        cartRes,
        successMessage: isShowMessage
          ? translateAlertAction('message_remove_product_successfully').replace(
              '{product}',
              capitalizeText(payload.name),
            )
          : '',
        errorMessage: isShowMessage ? translateAlertAction('message_remove_failed') : '',
        isReloadData,
      });
    }
    return cartRes;
  };

  const removeAllCartItems = async (isReloadData = false, isShowMessage = true) => {
    // add isDeleteAll to remove all items in cart
    const payload = { isDeleteAll: true };
    const { cartNo, cartItems } = getState();
    // :TODO: LOGS info for activity
    payload.cartNo = cartNo;
    // payload.eventSource = mapSourceToEnum({ isRecommended, isSameCategoryProduct }, router.pathname) || '';
    // payload.eventScreen = mapScreenToEnum({ isRecommended, isSameCategoryProduct }, router.pathname) || '';
    // end log

    // GA tracking remove all items from cart
    const arrSellerCodes = cartItems.map((i) => i.sellerCode);
    await Promise.all([...new Set(arrSellerCodes)].map((code) => getSellerByCode({ code }))); // save seller info into cache
    const promises = cartItems.map(
      (item) =>
        new Promise((resolve) => {
          getSellerByCode({ code: item.sellerCode })
            .then((sellerInfo) => {
              gtag.removeFromCart({
                ...item,
                sellerInfo,
              });
            })
            .finally(() => resolve(true));
        }),
    );
    await Promise.allSettled(promises);

    const cartRes = await CartClientV2.removeCartItem(payload);

    if (isReloadData) {
      await reloadDataCart({
        cartRes,
        successMessage: isShowMessage ? translateAlertAction('delete_cart_success') : '',
        errorMessage: isShowMessage ? translateAlertAction('delete_cart_failed') : '',
        isReloadData,
      });
    }
    return cartRes;
  };

  const addImportant = async (payload) => {
    const cartRes = await CartClientV2.updateCartItemImportant({
      eventSource: mapSourceToEnum({}, router.pathname) || '',
      eventScreen: mapScreenToEnum({}, router.pathname) || '',
      sku: payload.sku,
      isImportant: true,
      type: payload.type,
    });
    updateCart({
      cartRes,
      successMessage: translateAlertAction('message_mark_important_success'),
      errorMessage: translateAlertAction('message_mark_important_fail'),
    });
  };

  const removeImportant = async (payload, isReloadData = true) => {
    const cartRes = await CartClientV2.updateCartItemImportant({
      eventSource: mapSourceToEnum({}, router.pathname) || '',
      eventScreen: mapScreenToEnum({}, router.pathname) || '',
      sku: payload.sku,
      isImportant: false,
      type: payload.type,
    });
    if (isReloadData)
      updateCart({
        cartRes,
        successMessage: translateAlertAction('delete_important_success'),
        errorMessage: translateAlertAction('delete_important_failed'),
      });
  };

  const updateDeliveryInfo = async (body) => {
    const res = await CartClientV2.updateDeliveryMethod({
      ...body,
    });
    if (!isValid(res)) {
      return;
    }
    updateCart();
  };

  const updateDeliveryMethod = async ({
    deliveryMethod,
    customerDistrictCode,
    customerProvinceCode,
    customerWardCode,
    customerShippingAddress,
    info, // logs info
  }) => {
    const { cartNo } = getState();

    const res = await CartClientV2.updateDeliveryMethod({
      deliveryMethod,
      customerDistrictCode,
      customerProvinceCode,
      customerWardCode,
      customerShippingAddress,
      ...info,
      cartNo,
    });
    if (!isValid(res)) {
      NotifyUtils.error(
        res?.message || translateAlertAction('message_delivery_method_update_failed'),
      );
      return;
    }
    NotifyUtils.success(translateAlertAction('message_delivery_method_update_successfully'));
    updateCart();
  };

  const updatePaymentMethod = async ({
    paymentMethod,
    customerDistrictCode,
    customerProvinceCode,
    customerWardCode,
    info, // for logs
  }) => {
    const { cartNo } = getState();
    const res = await CartClientV2.updatePaymentMethod({
      paymentMethod,
      customerDistrictCode,
      customerProvinceCode,
      customerWardCode,
      ...info, // for log
      cartNo,
    });
    if (!isValid(res)) {
      NotifyUtils.error(
        res.message || translateAlertAction('message_payment_method_update_failed'),
      );
      return;
    }
    NotifyUtils.success(translateAlertAction('message_redeem_points'));
    updateCart();
  };

  const updateInvoice = async (body) => {
    const result = await CartClientV2.updateCart({ body });
    if (!isValid(result)) {
      NotifyUtils.error(
        result.message || translateAlertAction('message_update_invoice_information_failed'),
      );
    }
  };

  const updateCartInfo = async (body) => {
    const result = await CartClientV2.updateCart({ body });
    if (!isValid(result)) {
      NotifyUtils.error(result.message || translateAlertAction('message_failed_update_info'));
    } else {
      updateCart();
    }
  };

  const reOrderById = async ({ orderId }) => {
    const result = await CartService.reOrder({ orderId });
    if (isValid(result)) {
      updateCart();
    } else {
      NotifyUtils.error(result.message || translateAlertAction('message_cannot_reorder'));
    }
    return result;
  };

  // APO-8
  const selectCartItem = async ({ sku, skus, isSelected, isReloadCart, name, quantity }) => {
    dispatch({ type: SELECT_ITEM, payload: { sku, skus, isSelected } });
    if (isReloadCart) {
      reloadDataCart({
        successMessage: translateAlertAction('message_add_products_successfully'),
        errorMessage: translateAlertAction('message_update_product_failed'),
      });
    }
    const result = await CartService.selectCartItem({
      sku,
      skus,
      isSelected,
      cartNo: state?.cartNo || '',
      quantity,
      name,
    });
    // update lai cart sau khi sellected
    await updateCart();
    return result;
  };

  const selectAllCartItem = async () => {
    dispatch({ type: SELECT_ALL_ITEM });
    const result = await CartService.selectAllCartItem({ cartNo: state?.cartNo || '' });
    updateCart();
    return result;
  };

  const unselectAllCartItem = async () => {
    dispatch({ type: UN_SELECT_ALL_ITEM });
    const result = await CartService.unSelectAllCartItem({ cartNo: state?.cartNo || '' });
    updateCart();
    return result;
  };

  const verifyPayment = async (body) => {
    const rs = await CartClientV2.verifyPayment({ body });
    return rs;
  };

  const togglePriceFluctuationModal = (visible, { checkoutAfterConfirm = false } = {}) => {
    setShouldCheckoutAfterConfirm(visible && checkoutAfterConfirm);
    setOpenPriceFluctuationModal(visible);
  };

  // Khách chấp nhận giá mới -> BE chốt lại currentPrice của từng sku, cảnh báo tắt ở
  // lần load giỏ hàng kế tiếp.
  const confirmPriceFluctuation = async () => {
    const { cartFluctuates = [] } = getState();
    const skus = cartFluctuates.map((item) => item?.sku).filter(Boolean);
    const result = await CartService.confirmChangePrice({ skus });
    if (!isValidWithoutData(result)) {
      NotifyUtils.error(result?.message || translateAlertAction('error_pls_try_again'));
      return result;
    }
    await updateCart();
    return result;
  };

  /* Mã giảm giá trong giỏ không còn đủ điều kiện. BE bỏ canUse khi false (omitempty)
     nên phải kiểm bằng !canUse chứ không so sánh với false. */
  const getInvalidPromoCodes = () => {
    const { redeemApplyResult: currentRedeemApplyResult = [] } = getState();
    return (currentRedeemApplyResult || [])
      .filter((item) => item && !item.canUse && item.code)
      .map((item) => item.code);
  };

  // Cửa duy nhất đi sang /checkout từ giỏ hàng: còn mã giảm giá không đủ điều kiện thì
  // hỏi gỡ trước, còn item biến động giá thì bắt xác nhận trước, không cho qua thẳng.
  const goToCheckout = () => {
    const { cartFluctuates = [] } = getState();
    if (getInvalidPromoCodes().length > 0) {
      toggleShowRemoveInvalidPromos(true);
      return;
    }
    if (cartFluctuates.length > 0) {
      togglePriceFluctuationModal(true, { checkoutAfterConfirm: true });
      return;
    }
    router.push(CHECKOUT_URL);
  };

  /* Gate biến động giá cho luồng đặt hàng, dùng danh sách BE trả kèm response
     PUT /cart/verify (validateCart) — request mà luồng đặt hàng vốn đã gọi.

     KHÔNG được quay lại cách gọi updateCart() (GET /cart/v2) ở đây. /cart/v2 khi cache hit
     sẽ trả cache ngay rồi recompute giỏ trong goroutine nền và ghi lại cache bằng Upsert.
     Lệnh ghi muộn đó có thể hoàn tất SAU khi checkout đã xoá cart, tái tạo document cache
     đã bị xoá => cache có cart, main DB không có, và mọi API mutate sau đó trả
     "Not found any matched cart".

     Trả về true = còn biến động giá, modal đã mở và KHÔNG được đi tiếp luồng đặt hàng.
     Quyết định gate đọc thẳng từ tham số, không đọc lại state, để không phụ thuộc thời
     điểm React commit xong dispatch bên dưới. */
  const applyPriceFluctuationFromVerify = (fluctuatedItems = []) => {
    const items = fluctuatedItems || [];
    // Không biến động thì thoát sớm, KHÔNG dispatch: reducer luôn tạo mảng cartItems mới
    // nên dispatch thừa sẽ re-render toàn bộ danh sách sản phẩm đúng lúc đang submit đơn.
    // Trạng thái cũ (nếu có) đã được reset bởi updateCart() trong confirmPriceFluctuation.
    if (items.length === 0) return false;
    dispatch({ type: SET_CART_FLUCTUATES, payload: { fluctuatedItems: items } });
    togglePriceFluctuationModal(true);
    return true;
  };

  const confirmPriceFluctuationFromCart = async () => {
    // đọc cờ trước khi đóng modal vì togglePriceFluctuationModal(false) sẽ reset nó
    const goCheckoutAfterConfirm = shouldCheckoutAfterConfirm;
    const result = await confirmPriceFluctuation();
    if (!isValidWithoutData(result)) return;
    togglePriceFluctuationModal(false);
    if (goCheckoutAfterConfirm) {
      router.push(CHECKOUT_URL);
    }
  };
  // chặn không quá 200 item
  const validate200Item = (sku) => {
    const { mapQuantity } = getState();
    const quantity = mapQuantity?.get(sku) || 0;
    if (quantity === 0 && mapQuantity.size >= MAX_ITEM_IN_CART) {
      NotifyUtils.error(
        translateAlertAction('message_validate_num_items').replace('{number}', MAX_ITEM_IN_CART),
      );
      // toggleShowModalMax200Item();
      return false;
    }
    return true;
  };

  const handleScrollToGiftRow = () => {
    giftProductRowRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'start',
    });
  };

  const multiVoucherHandleChangePromoV2 = async (currentPromoCode) => {
    const { cartNo } = getState();
    const res = await CartClientV2.putChangeMultiRedeem(currentPromoCode, cartNo);

    return res;
  };

  const multiVoucherHandleRemovePromoV2 = async (currentPromoCode) => {
    const { cartNo } = getState();
    const res = await CartClientV2.putRemoveMultiRedeem(currentPromoCode, cartNo);

    return res;
  };

  /* Gỡ hết mã không đủ điều kiện rồi đi tiếp. getState đọc qua ref nên sau await
     reloadDataCart là đã thấy giỏ mới, gọi lại goToCheckout an toàn. Nếu mã vẫn còn
     thì dừng lại để khách thao tác tiếp, tránh mở lại dialog ngay lập tức. */
  const removeInvalidPromosAndContinue = async () => {
    if (isRemovingInvalidPromos) return;
    const codes = getInvalidPromoCodes();
    if (codes.length === 0) {
      toggleShowRemoveInvalidPromos(false);
      return;
    }
    setIsRemovingInvalidPromos(true);
    try {
      const res = await multiVoucherHandleRemovePromoV2(codes);
      await reloadDataCart({
        cartRes: res,
        successMessage: translateCommon('cart-info.delete_voucher_success'),
        errorMessage: translateCommon('cart-info.delete_voucher_failed'),
        isReloadData: true,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsRemovingInvalidPromos(false);
    }
    toggleShowRemoveInvalidPromos(false);
    if (getInvalidPromoCodes().length === 0) {
      goToCheckout();
    }
  };

  const removeVoucherByCode = async (code) => {
    try {
      const res = await multiVoucherHandleRemovePromoV2([code]);
      await reloadDataCart({
        cartRes: res,
        successMessage: translateCommon('cart-info.delete_voucher_success'),
        errorMessage: translateCommon('cart-info.delete_voucher_failed'),
        isReloadData: true,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const contextValues = {
    mapDataProduct,
    removeCartItem,
    removeAllCartItems,
    updateCartItem,
    updateCartItemMV2,
    increase,
    decrease,
    clearCart,
    handleCheckout,
    addImportant,
    removeImportant,
    updateCart,
    updateDeliveryMethod,
    updatePaymentMethod,
    reloadDataCart,
    getCartItemsInfo,
    updateInvoice,
    updateDeliveryInfo,
    updateCartInfo,
    reOrderById,
    updateCartItems,
    updateCartItemByExecel,
    selectCartItem,
    selectAllCartItem,
    unselectAllCartItem,
    verifyPayment,
    confirmPriceFluctuation,
    confirmPriceFluctuationFromCart,
    applyPriceFluctuationFromVerify,
    goToCheckout,
    isOpenPriceFluctuationModal,
    togglePriceFluctuationModal,
    validate200Item,
    getErrorCart,
    toggleShowModalMax200Item,
    isShowModalMax200Item,
    giftProductRowRef,
    handleScrollToGiftRow,
    isLoadingCart,
    getState,
    isDeletingPromo,
    togglePromoList,
    tempRedeemApplyResult,
    setTempRedeemApplyResult,
    toggleOnCheckingPromo,
    multiVoucherHandleChangePromoV2,
    multiVoucherHandleRemovePromoV2,
    removeVoucherByCode,
    invalidPromoCodes: getInvalidPromoCodes(),
    isShowRemoveInvalidPromos,
    toggleShowRemoveInvalidPromos,
    isRemovingInvalidPromos,
    removeInvalidPromosAndContinue,
    ...state,
  };

  return <CartContext.Provider value={contextValues}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
